import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { useToast } from "@/components/toast";
import { AdminPage, Card, Gate, H, Meta } from "@/components/admin-ui";
import { Button, ChipGroup, ErrorState, Field, Loading, Toggle } from "@/components/ui";

export default function AdminConfig() {
  return <Gate perm="config" testId="admin-config-forbidden"><Inner /></Gate>;
}

const num = (t) => parseInt(String(t).replace(/\D/g, "") || "0", 10);

function Row({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span className="text-sm font-medium">{label}</span>
      <Toggle testId={testId} label={label} checked={value} onChange={onChange} />
    </div>
  );
}

function Inner() {
  const toast = useToast();
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["admin-config"], queryFn: () => api("/admin/config") });
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { if (data) setV(data); }, [data]);
  if (isLoading || (!v && !isError)) return <Loading />;
  if (isError || !v) return <ErrorState testId="admin-config-error" message={error?.message ?? ""} onRetry={() => refetch()} />;

  const save = async (section, body) => {
    setBusy(section);
    try { await api(`/admin/config/${section}`, { method: "PUT", body }); toast("Saved — changes are live", "success"); refetch(); }
    catch (e) { toast(e.message, "error"); } finally { setBusy(null); }
  };
  const methodLabel = (id) => v.options.payment_methods.find((m) => m.id === id)?.label ?? id;
  const sum = Object.values(v.matching.weights).reduce((a, b) => a + (Number(b) || 0), 0);
  const setVer = (patch) => setV({ ...v, verification: { ...v.verification, ...patch } });
  const setMat = (patch) => setV({ ...v, matching: { ...v.matching, ...patch } });
  const setMis = (id, patch) => setV({ ...v, missions: { ...v.missions, [id]: { ...v.missions[id], ...patch } } });
  const setPlan = (i, patch) => setV({ ...v, membership: { ...v.membership, plans: v.membership.plans.map((x, j) => (j === i ? { ...x, ...patch } : x)) } });

  return (
    <AdminPage title="Config" testId="admin-config-screen">
      <div className="grid gap-4 xl:grid-cols-2">
        <div>
          <H>Verification</H>
          <Card testId="config-verification" className="mt-2">
            <Field testId="config-fee-input" label="Verification fee" inputMode="numeric" value={String(v.verification.fee_amount)} onChange={(e) => setVer({ fee_amount: num(e.target.value) })} />
            <p className="mb-2 text-sm font-bold">Currency</p>
            <ChipGroup testIdPrefix="config-currency" multi={false} options={v.options.currencies} value={[v.verification.currency]} onChange={(c) => c[0] && setVer({ currency: c[0] })} />
            <p className="mb-2 mt-4 text-sm font-bold">Enabled payment methods</p>
            <ChipGroup testIdPrefix="config-method" options={v.options.payment_methods.map((m) => m.label)} value={v.verification.payment_methods.map(methodLabel)}
              onChange={(labels) => setVer({ payment_methods: v.options.payment_methods.filter((m) => labels.includes(m.label)).map((m) => m.id) })} />
            <div className="mt-3"><Row testId="config-id-back-switch" label="ID back required" value={v.verification.id_back_required} onChange={(b) => setVer({ id_back_required: b })} /></div>
            <Row testId="config-selfie-switch" label="Live selfie required" value={v.verification.selfie_required} onChange={(b) => setVer({ selfie_required: b })} />
            <Button testId="config-verification-save" title="Save verification" loading={busy === "verification"} className="mt-3" onClick={() => save("verification", v.verification)} />
          </Card>
          <H>Requests</H>
          <Card testId="config-requests" className="mt-2">
            <Field testId="config-expiry-input" label="Request expiry (days)" inputMode="numeric" value={String(v.requests.expiry_days)} onChange={(e) => setV({ ...v, requests: { expiry_days: num(e.target.value) } })} />
            <Button testId="config-requests-save" title="Save requests" loading={busy === "requests"} onClick={() => save("requests", v.requests)} />
          </Card>
          <H>Matching</H>
          <Card testId="config-matching" className="mt-2">
            <div className="grid grid-cols-2 gap-x-3 sm:grid-cols-3">
              {v.options.weight_keys.map((k) => (
                <Field key={k} testId={`config-weight-${k}`} label={k} inputMode="numeric" value={String(v.matching.weights[k] ?? 0)} onChange={(e) => setMat({ weights: { ...v.matching.weights, [k]: num(e.target.value) } })} />
              ))}
            </div>
            <p data-testid="config-weights-sum" className={`mb-4 text-sm font-bold ${sum !== 100 ? "text-[#9E3C3E]" : "text-[#3A6C54]"}`}>Total: {sum} / 100{sum !== 100 ? " — must equal 100" : ""}</p>
            <Field testId="config-boost-input" label="Verified member ranking boost (points)" inputMode="numeric" value={String(v.matching.verified_boost)} onChange={(e) => setMat({ verified_boost: num(e.target.value) })} />
            <Row testId="config-cross-mission-switch" label="Cross-mission visibility (treat all missions as equally compatible)" value={v.matching.cross_mission_visibility} onChange={(b) => setMat({ cross_mission_visibility: b })} />
            <Meta>Off (default): compatible missions are prioritised using the mission compatibility matrix.</Meta>
            <Button testId="config-matching-save" title="Save matching" disabled={sum !== 100} loading={busy === "matching"} className="mt-3" onClick={() => save("matching", v.matching)} />
          </Card>
        </div>
        <div>
          <H>Mission categories</H>
          <div className="mt-2 flex flex-col gap-3">
            {Object.entries(v.missions).map(([id, c]) => (
              <Card key={id} testId={`config-mission-${id}`}>
                <Row testId={`config-mission-${id}-enabled`} label={`${id} enabled`} value={c.enabled} onChange={(b) => setMis(id, { enabled: b })} />
                <Field testId={`config-mission-${id}-name`} label="Display name" value={c.display_name} onChange={(e) => setMis(id, { display_name: e.target.value })} />
                <Field testId={`config-mission-${id}-description`} label="Description" multiline value={c.description} onChange={(e) => setMis(id, { description: e.target.value })} />
                <Field testId={`config-mission-${id}-button`} label="Request button label" value={c.button_label} onChange={(e) => setMis(id, { button_label: e.target.value })} />
              </Card>
            ))}
            <Button testId="config-missions-save" title="Save mission categories" loading={busy === "missions"} onClick={() => save("missions", { categories: v.missions })} />
          </div>
          <H>Membership</H>
          <Card testId="config-membership" className="mt-2">
            <Field testId="config-trial-days-input" label="Free trial length (days)" inputMode="numeric" value={String(v.membership.trial_days)} onChange={(e) => setV({ ...v, membership: { ...v.membership, trial_days: num(e.target.value) } })} />
            <Row testId="config-yearly-verification-switch" label="Yearly plan includes free verification" value={v.membership.yearly_includes_verification} onChange={(b) => setV({ ...v, membership: { ...v.membership, yearly_includes_verification: b } })} />
            {v.membership.plans.map((p, i) => (
              <div key={p.id} className="mt-3 rounded-xl bg-[#FDFBF7] p-3" data-testid={`config-plan-${p.id}`}>
                <Row testId={`config-plan-${p.id}-enabled`} label={`${p.name || p.id} plan enabled`} value={p.enabled} onChange={(b) => setPlan(i, { enabled: b })} />
                <Field testId={`config-plan-${p.id}-name`} label="Plan name" value={p.name} onChange={(e) => setPlan(i, { name: e.target.value })} />
                <div className="grid grid-cols-2 gap-3">
                  <Field testId={`config-plan-${p.id}-price`} label="Price (₦)" inputMode="numeric" value={String(p.price)} onChange={(e) => setPlan(i, { price: num(e.target.value) })} />
                  <Field testId={`config-plan-${p.id}-days`} label="Duration (days)" inputMode="numeric" value={String(p.days)} onChange={(e) => setPlan(i, { days: num(e.target.value) })} />
                </div>
              </div>
            ))}
            <Meta>Savings badges are calculated against the Monthly price. Payments are one-time (manual renewal).</Meta>
            <Button testId="config-membership-save" title="Save membership" loading={busy === "membership"} className="mt-3" onClick={() => save("membership", v.membership)} />
          </Card>
        </div>
      </div>
    </AdminPage>
  );
}
